import { datepickerToISOFormat, YesObject } from '@yes.technology/react-toolkit'
import { uniqBy } from 'lodash'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18n-lite'
import { useSearchParams } from 'react-router-dom'
import {
  PatchObjectResponse,
  PostObjectResponse
} from 'shared/types/object.type'
import { AllowedLocales } from 'shared/utils/date/formatDate'
import useObjectFields from 'site/Renderer/components/ObjectView/hooks/useObjectFields'
import patchObject from 'yesObject/shared/api/patchObject'
import postObject from 'yesObject/shared/api/postObject'
import useYesObject from 'yesObject/shared/hooks/useYesObject'
import useTableContext from '../useTableContext'

export const SITUATION_CODE_ACTIVE = '40'

type UseTableActionSaveProps = {
  initialStatusUuid: string
}

export default function useTableActionSave({
  initialStatusUuid
}: UseTableActionSaveProps) {
  const { language } = useTranslation()

  const [searchParams] = useSearchParams()

  const {
    idObjectClass = '',
    setMode,
    changedFieldValues,
    setChangedFieldValues,
    objects,
    fields,
    refetchObjects,
    mode,
    relationCounterpartField
  } = useTableContext()

  const mainObjectUuid = searchParams.get('object-uuid') || ''
  const mainObjectObjectClass = searchParams.get('uuid_objectclass') || ''

  const { fetchObjects } = useYesObject({
    objectUUID: mainObjectUuid,
    objectclassUUID: mainObjectObjectClass,
    skipMetadata: true,
    skipRelations: false,
    enabled: false
  })

  const { relationFields } = useObjectFields({
    objectclassUuid: mainObjectObjectClass
  })

  const relationColumnName = useMemo(() => {
    return (
      relationFields.find(
        (relation) => relation?.relation?.object_class?.uuid === idObjectClass
      )?.column_json || ''
    )
  }, [idObjectClass, relationFields])

  const relationCounterpartColumnName = useMemo(() => {
    return Object.keys(relationCounterpartField ?? {})[0]
  }, [relationCounterpartField])

  const associateRelationsWithMainObject = async (
    objects: YesObject[] = []
  ) => {
    if (mode !== 'insert' || objects.length === 0) {
      return
    }

    const mainObjectResult = await fetchObjects()

    if (
      !mainObjectUuid ||
      !relationColumnName ||
      mainObjectResult?.data?.objects?.length === 0
    ) {
      return
    }

    const lastVersion = mainObjectResult?.data?.objects?.[0]?.version || 0
    const currentRelations = (mainObjectResult?.data?.objects?.[0]?.[
      relationColumnName
    ] || []) as YesObject[]

    const result = await patchObject({
      uuid: mainObjectUuid,
      data: {
        version: lastVersion,
        [relationColumnName]: uniqBy(
          [
            ...currentRelations.map((relation) => ({ uuid: relation.uuid })),
            ...objects.map((obj) => ({ uuid: obj.uuid }))
          ],
          'uuid'
        )
      },
      showLoading: true
    })

    return result
  }

  const save = async () => {
    const saveRequestPromises = Object.entries(changedFieldValues).map(
      async ([objectUuidOrIndex, changes]): Promise<
        PatchObjectResponse | PostObjectResponse
      > => {
        const normalizedChanges = Object.fromEntries(
          Object.entries(changes)
            .filter(([columnName]) => {
              return columnName !== relationCounterpartColumnName
            })
            .flatMap(([columnName, value]) => {
              const { type = 'STRING' } = fields?.[columnName] || {}

              if (mode === 'insert' && value.length === 0) {
                return []
              }

              if (type === 'DATETIME') {
                return [
                  [
                    columnName,
                    datepickerToISOFormat(
                      typeof value === 'string' ? value : '',
                      {
                        locale: language as AllowedLocales,
                        timezone: window.waTimezone
                      }
                    ) || ''
                  ]
                ]
              }

              return [[columnName, value]]
            })
        )

        const response = await (mode === 'insert'
          ? postObject({
              uuid_Objectclass: idObjectClass,
              uuid_status: initialStatusUuid,
              situation: SITUATION_CODE_ACTIVE,
              data: normalizedChanges,
              showLoading: true
            })
          : patchObject({
              uuid: objectUuidOrIndex,
              data: {
                ...normalizedChanges,
                version: objects.find(({ uuid }) => uuid === objectUuidOrIndex)
                  ?.version
              },
              showLoading: true
            }))

        if (!response.success) {
          throw new Error(response.message)
        }

        return response
      }
    )

    const result = await Promise.all(saveRequestPromises)

    const objectsPendingAssociation = result.flatMap(
      (obj) => obj.data
    ) as YesObject[]

    await associateRelationsWithMainObject(objectsPendingAssociation)

    refetchObjects()

    setChangedFieldValues({})
    setMode('view')

    return result
  }

  return save
}
